import { useMemo, useState } from 'react';
import { api, ApiError, formatDateTime, scr } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import type { Page, SubsidyClaim, SubsidyScheme } from '../../lib/types';
import { useApiQuery } from '../../lib/useApi';
import { Detail, EmptyState, ErrorNote, Field, Modal, PageHeader, RoleNotice, Spinner, StatusBadge, TabStrip } from '../../ui/components';

const PRODUCTION_TYPES = ['CROP_FARMING', 'LIVESTOCK', 'POULTRY', 'DAIRY', 'APICULTURE', 'HORTICULTURE', 'MIXED_FARMING'] as const;

type Tab = 'schemes' | 'claims';

/** M33 Agriculture — subsidy schemes (config catalogue) and farmer subsidy claims (review, approve, disburse via SeyPay). */
export function Subsidies() {
  const [tab, setTab] = useState<Tab>('claims');
  return (
    <div>
      <PageHeader title="Subsidy Schemes & Claims" subtitle="Input-support and equipment-grant schemes, and farmer claims against them (M33)." />
      <TabStrip tabs={[{ key: 'claims', label: 'Claims' }, { key: 'schemes', label: 'Schemes' }]} active={tab} onChange={setTab} />
      {tab === 'claims' ? <ClaimsTab /> : <SchemesTab />}
    </div>
  );
}

// ── Claims ────────────────────────────────────────────────────────────────

function ClaimsTab() {
  const { persona } = useAuth();
  const isSchemeAdmin = persona ? persona.roles.includes('SCHEME_ADMIN') : false;
  const [status, setStatus] = useState('');
  const [q, setQ] = useState('');
  const [appliedQ, setAppliedQ] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const path = useMemo(() => {
    const params = new URLSearchParams({ limit: '50' });
    if (status) params.set('status', status);
    if (appliedQ) params.set('q', appliedQ);
    return `/api/v1/m33/subsidy-claims?${params.toString()}`;
  }, [status, appliedQ]);
  const listQ = useApiQuery<Page<SubsidyClaim>>(path);

  return (
    <div>
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <form
          className="flex items-center gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            setAppliedQ(q);
          }}
        >
          <input className="field-input !w-56" placeholder="Claim ref, applicant…" value={q} onChange={(e) => setQ(e.target.value)} />
          <select className="field-input !w-auto" value={status} onChange={(e) => setStatus(e.target.value)}>
            <option value="">All statuses</option>
            {['SUBMITTED', 'APPROVED', 'REJECTED', 'DISBURSE_PENDING', 'DISBURSED', 'WITHDRAWN'].map((s) => (
              <option key={s} value={s}>
                {s.replaceAll('_', ' ')}
              </option>
            ))}
          </select>
          <button type="submit" className="btn-secondary">
            Search
          </button>
        </form>
      </div>
      {!isSchemeAdmin ? <RoleNotice roles={['SCHEME_ADMIN']} /> : null}
      {listQ.error ? <ErrorNote message={listQ.error} /> : null}
      <div className="card overflow-x-auto">
        {listQ.loading ? (
          <Spinner />
        ) : (listQ.data?.items.length ?? 0) === 0 ? (
          <EmptyState title="No subsidy claims match" />
        ) : (
          <table className="w-full">
            <thead>
              <tr>
                <th className="table-head">Claim ref</th>
                <th className="table-head">Applicant</th>
                <th className="table-head">Requested</th>
                <th className="table-head">Approved</th>
                <th className="table-head">Status</th>
              </tr>
            </thead>
            <tbody>
              {listQ.data!.items.map((c) => (
                <tr key={c.id} className="cursor-pointer hover:bg-slate-50" onClick={() => setSelectedId(c.id)}>
                  <td className="table-cell font-mono text-xs font-medium">{c.claimRef}</td>
                  <td className="table-cell font-mono text-xs">{c.applicantId}</td>
                  <td className="table-cell text-xs">{scr(c.requestedAmountScr)}</td>
                  <td className="table-cell text-xs">{scr(c.approvedAmountScr)}</td>
                  <td className="table-cell">
                    <StatusBadge status={c.status} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      {selectedId ? (
        <ClaimDetailModal
          id={selectedId}
          isSchemeAdmin={isSchemeAdmin}
          onClose={() => setSelectedId(null)}
          onChanged={() => {
            listQ.reload();
          }}
        />
      ) : null}
    </div>
  );
}

function ClaimDetailModal({ id, isSchemeAdmin, onClose, onChanged }: { id: string; isSchemeAdmin: boolean; onClose: () => void; onChanged: () => void }) {
  const cq = useApiQuery<SubsidyClaim>(`/api/v1/m33/subsidy-claims/${id}`);
  const c = cq.data;
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showDecision, setShowDecision] = useState<'approve' | 'reject' | null>(null);
  const [note, setNote] = useState('');
  const [approvedAmount, setApprovedAmount] = useState('');

  const call = async (fn: () => Promise<unknown>) => {
    setBusy(true);
    setError(null);
    try {
      await fn();
      cq.reload();
      onChanged();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Request failed');
    } finally {
      setBusy(false);
    }
  };
  const disburse = () => call(() => api.post(`/api/v1/m33/subsidy-claims/${id}/disburse`));
  const confirm = () =>
    call(() => {
      if (showDecision === 'approve') {
        return api.post(`/api/v1/m33/subsidy-claims/${id}/review`, {
          approve: true,
          approvedAmountScr: approvedAmount ? Number.parseFloat(approvedAmount) : undefined,
          note: note || undefined,
        });
      }
      return api.post(`/api/v1/m33/subsidy-claims/${id}/review`, { approve: false, rejectionReason: note });
    });

  return (
    <Modal title={c ? c.claimRef : 'Subsidy claim'} onClose={onClose} wide>
      {cq.error ? <ErrorNote message={cq.error} /> : null}
      {cq.loading ? <Spinner /> : null}
      {error ? (
        <div className="mb-3">
          <ErrorNote message={error} />
        </div>
      ) : null}
      {c ? (
        <div className="space-y-5">
          <div className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm sm:grid-cols-3">
            <Detail label="Status" value={<StatusBadge status={c.status} />} />
            <Detail label="Applicant" value={`${c.applicantType}:${c.applicantId}`} mono />
            <Detail label="Requested" value={scr(c.requestedAmountScr)} />
            <Detail label="Approved" value={scr(c.approvedAmountScr)} />
            <Detail label="Disbursed" value={formatDateTime(c.disbursedAt)} />
            <Detail label="Fee intent ref" value={c.payIntentRefNo ?? '—'} mono />
          </div>
          <Detail label="Description" value={c.description} />
          {c.rejectionReason ? <ErrorNote message={`Rejected: ${c.rejectionReason}`} /> : null}
          <div>
            <div className="field-label">Receipts</div>
            <table className="w-full text-sm">
              <thead>
                <tr>
                  <th className="table-head">Description</th>
                  <th className="table-head">Amount</th>
                </tr>
              </thead>
              <tbody>
                {c.receipts.map((r) => (
                  <tr key={r.id}>
                    <td className="table-cell">{r.description}</td>
                    <td className="table-cell">{scr(r.amountScr)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {isSchemeAdmin ? (
            <div className="border-t border-slate-100 pt-4">
              {!showDecision ? (
                <div className="flex flex-wrap gap-2">
                  {c.status === 'SUBMITTED' ? (
                    <>
                      <button className="btn-success" disabled={busy} onClick={() => setShowDecision('approve')}>
                        Approve
                      </button>
                      <button className="btn-danger" disabled={busy} onClick={() => setShowDecision('reject')}>
                        Reject
                      </button>
                    </>
                  ) : null}
                  {c.status === 'APPROVED' ? (
                    <button className="btn-primary" disabled={busy} onClick={disburse}>
                      {busy ? 'Opening disbursement…' : 'Open SeyPay disbursement intent'}
                    </button>
                  ) : null}
                </div>
              ) : (
                <div className="space-y-2 rounded-lg border border-slate-200 p-3">
                  {showDecision === 'approve' ? (
                    <Field label={`Approved amount (SCR) — max ${c.requestedAmountScr}`}>
                      <input className="field-input" type="number" placeholder={String(c.requestedAmountScr)} value={approvedAmount} onChange={(e) => setApprovedAmount(e.target.value)} />
                    </Field>
                  ) : null}
                  <Field label={showDecision === 'approve' ? 'Note (optional)' : 'Rejection reason'}>
                    <textarea className="field-input" rows={2} value={note} onChange={(e) => setNote(e.target.value)} />
                  </Field>
                  <div className="flex gap-2">
                    <button className="btn-secondary" onClick={() => setShowDecision(null)}>
                      Cancel
                    </button>
                    <button className={showDecision === 'approve' ? 'btn-success' : 'btn-danger'} disabled={busy} onClick={confirm}>
                      {busy ? 'Submitting…' : 'Confirm'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          ) : null}
        </div>
      ) : null}
    </Modal>
  );
}

// ── Schemes ───────────────────────────────────────────────────────────────

function SchemesTab() {
  const { persona } = useAuth();
  const isSchemeAdmin = persona ? persona.roles.includes('SCHEME_ADMIN') : false;
  const [includeInactive, setIncludeInactive] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const path = useMemo(() => `/api/v1/m33/subsidy-schemes?limit=100${includeInactive ? '&status=all' : ''}`, [includeInactive]);
  const listQ = useApiQuery<Page<SubsidyScheme>>(path);

  const toggleActive = async (scheme: SubsidyScheme) => {
    await api.post(`/api/v1/m33/subsidy-schemes/${scheme.code}/toggle-active`, { active: !scheme.active });
    listQ.reload();
  };

  return (
    <div>
      <div className="mb-4 flex items-center justify-between">
        <label className="flex items-center gap-2 text-sm text-slate-600">
          <input type="checkbox" checked={includeInactive} onChange={(e) => setIncludeInactive(e.target.checked)} />
          Show inactive schemes
        </label>
        {isSchemeAdmin ? (
          <button className="btn-primary" onClick={() => setShowCreate(true)}>
            + New scheme
          </button>
        ) : null}
      </div>
      {listQ.error ? <ErrorNote message={listQ.error} /> : null}
      <div className="card overflow-x-auto">
        {listQ.loading ? (
          <Spinner />
        ) : (listQ.data?.items.length ?? 0) === 0 ? (
          <EmptyState title="No subsidy schemes configured" />
        ) : (
          <table className="w-full">
            <thead>
              <tr>
                <th className="table-head">Code</th>
                <th className="table-head">Name</th>
                <th className="table-head">Type</th>
                <th className="table-head">Rate</th>
                <th className="table-head">Max claim</th>
                <th className="table-head">FY</th>
                <th className="table-head">Active</th>
                {isSchemeAdmin ? <th className="table-head"></th> : null}
              </tr>
            </thead>
            <tbody>
              {listQ.data!.items.map((s) => (
                <tr key={s.id}>
                  <td className="table-cell font-mono text-xs font-medium">{s.code}</td>
                  <td className="table-cell">{s.name}</td>
                  <td className="table-cell text-xs">{s.schemeType.replaceAll('_', ' ')}</td>
                  <td className="table-cell text-xs">{s.subsidyRatePercent}%</td>
                  <td className="table-cell text-xs">{scr(s.maxClaimAmountScr)}</td>
                  <td className="table-cell text-xs">{s.fiscalYear}</td>
                  <td className="table-cell">
                    <StatusBadge status={s.active ? 'ACTIVE' : 'CLOSED'} />
                  </td>
                  {isSchemeAdmin ? (
                    <td className="table-cell">
                      <button className="text-xs font-semibold text-brand-700 underline" onClick={() => toggleActive(s)}>
                        {s.active ? 'Deactivate' : 'Activate'}
                      </button>
                    </td>
                  ) : null}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      {showCreate ? (
        <NewSchemeModal
          onClose={() => setShowCreate(false)}
          onCreated={() => {
            setShowCreate(false);
            listQ.reload();
          }}
        />
      ) : null}
    </div>
  );
}

function NewSchemeModal({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [schemeType, setSchemeType] = useState<'INPUT_SUPPORT' | 'EQUIPMENT_GRANT'>('INPUT_SUPPORT');
  const [description, setDescription] = useState('');
  const [eligible, setEligible] = useState<string[]>([]);
  const [rate, setRate] = useState('50');
  const [maxAmount, setMaxAmount] = useState('');
  const [fiscalYear, setFiscalYear] = useState(String(new Date().getFullYear()));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const toggle = (v: string) => setEligible((prev) => (prev.includes(v) ? prev.filter((x) => x !== v) : [...prev, v]));

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.post('/api/v1/m33/subsidy-schemes', {
        code,
        name,
        schemeType,
        description: description || undefined,
        eligibleProductionTypes: eligible,
        subsidyRatePercent: Number.parseFloat(rate),
        maxClaimAmountScr: Number.parseFloat(maxAmount),
        fiscalYear: Number.parseInt(fiscalYear, 10),
      });
      onCreated();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Request failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title="New subsidy scheme" onClose={onClose} wide>
      {error ? (
        <div className="mb-3">
          <ErrorNote message={error} />
        </div>
      ) : null}
      <div className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <Field label="Code">
            <input className="field-input" value={code} onChange={(e) => setCode(e.target.value)} placeholder="INPUT_2026" />
          </Field>
          <Field label="Scheme type">
            <select className="field-input" value={schemeType} onChange={(e) => setSchemeType(e.target.value as 'INPUT_SUPPORT' | 'EQUIPMENT_GRANT')}>
              <option value="INPUT_SUPPORT">Input support</option>
              <option value="EQUIPMENT_GRANT">Equipment grant</option>
            </select>
          </Field>
        </div>
        <Field label="Name">
          <input className="field-input" value={name} onChange={(e) => setName(e.target.value)} />
        </Field>
        <Field label="Description (optional)">
          <textarea className="field-input" rows={2} value={description} onChange={(e) => setDescription(e.target.value)} />
        </Field>
        <div>
          <div className="field-label">Eligible production types</div>
          <div className="flex flex-wrap gap-2">
            {PRODUCTION_TYPES.map((t) => (
              <label key={t} className={`cursor-pointer rounded-lg border px-2.5 py-1 text-xs font-medium ${eligible.includes(t) ? 'border-brand-500 bg-brand-50 text-brand-700' : 'border-slate-300 text-slate-600'}`}>
                <input type="checkbox" className="mr-1.5" checked={eligible.includes(t)} onChange={() => toggle(t)} />
                {t.replaceAll('_', ' ')}
              </label>
            ))}
          </div>
        </div>
        <div className="grid grid-cols-3 gap-3">
          <Field label="Subsidy rate (%)">
            <input className="field-input" type="number" value={rate} onChange={(e) => setRate(e.target.value)} />
          </Field>
          <Field label="Max claim (SCR)">
            <input className="field-input" type="number" value={maxAmount} onChange={(e) => setMaxAmount(e.target.value)} />
          </Field>
          <Field label="Fiscal year">
            <input className="field-input" type="number" value={fiscalYear} onChange={(e) => setFiscalYear(e.target.value)} />
          </Field>
        </div>
        <button className="btn-primary w-full" disabled={busy || !code || !name || !maxAmount || eligible.length === 0} onClick={submit}>
          {busy ? 'Creating…' : 'Create scheme'}
        </button>
      </div>
    </Modal>
  );
}
